import { str_from_query } from "@/api";
import { axios_instance } from "@/api/api";
import { router } from "@/router/router";
import { defineStore } from "pinia";
import { ref, watch } from "vue";
import { toast } from "vue3-toastify";
import type { keylogger } from ".";

export const use_keylogger = defineStore("keylogger", () => {
  const keyloggers = ref<keylogger[]>([]);
  const loading = ref<{
    get_keyloggers: boolean;
  }>({
    get_keyloggers: false,
  });
  const current_page = ref<number>(1);
  const total_filter = ref<number>(0);
  const can_load_more = ref<boolean>(true);

  // In-flight promise so concurrent callers share one request instead of
  // firing duplicates (vueuse's immediate watcher races onMounted).
  let in_flight: Promise<unknown> | null = null;

  const get_keyloggers = () => {
    console.log("[PROBE] get_keyloggers called, page=", current_page.value, "in_flight=", !!in_flight);
    if (in_flight) return in_flight;
    loading.value.get_keyloggers = true;
    const data = JSON.stringify({
      agent_id: router.currentRoute.value.params.client_id,
      page: current_page.value,
      size: 5,
      keyword: str_from_query("keyword"),
    });

    const config = {
      method: "post",
      maxBodyLength: Infinity,
      url: "/api/v1/view/key-logger",
      headers: {
        "Content-Type": "application/json",
      },
      data: data,
    };

    in_flight = axios_instance
      .request<{
        data: keylogger[] | null;
        message: string;
        total_filter: number;
      }>(config)
      .then((response) => {
        keyloggers.value.push(...(response.data.data ?? []));
        total_filter.value = response.data.total_filter;
        current_page.value = current_page.value + 1;
        if (keyloggers.value.length >= total_filter.value) {
          can_load_more.value = false;
        }
      })
      .catch((error) => {
        toast.error(error);
      })
      .finally(() => {
        loading.value.get_keyloggers = false;
        in_flight = null;
      });
    return in_flight;
  };

  // No onMounted fetch: useInfiniteScroll's watcher runs with immediate:true
  // and owns the initial load. Fetching in both places races two callers and
  // double-fires the first page.
  const resetState = () => {
    keyloggers.value = [];
    current_page.value = 1;
    total_filter.value = 0;
    can_load_more.value = true;
  };

  watch(
    () => router.currentRoute.value,
    (to, from) => {
      if (to.name !== "ClientKeylogger") {
        resetState();
        return;
      }
      // Query-only navigation reuses this component, so onMounted never
      // re-runs; refetch here when the keyword filter changes.
      if (to.query.keyword !== from?.query.keyword) {
        resetState();
        get_keyloggers();
      }
    },
  );

  return {
    keyloggers,
    loading,
    total_filter,
    can_load_more,
    get_keyloggers,
  };
});
